import { AfterViewInit, Component, computed, inject, input, OnInit, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccountModel } from 'src/app/core/clients/generated/client';
import { fadeIn } from 'src/app/shared/constants/animations';
import { ConfirmationModalComponent } from 'src/app/shared/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalData } from 'src/app/shared/modals/confirmation-modal/modal-data.models';
import { RequestExitConfirmationData } from '../../account-request-workflows/shared/constants/workflow-navigation.constants';
import { AccountWorkflowNavModel, SolicitationOptionModel } from '../../account-request-workflows/shared/models/workflow.models';
import { CalculationStripComponent } from './components/calculation-strip/calculation-strip.component';
import { StepIndicatorComponent } from './components/step-indicator/step-indicator.component';
import { FundingStepComponent } from './components/funding-step/funding-step.component';
import { SleeveAmountStepComponent } from './components/sleeve-amount-step/sleeve-amount-step.component';
import { ReviewPositionsStepComponent } from './components/review-positions-step/review-positions-step.component';
import { BuySecuritiesStepComponent } from './components/buy-securities-step/buy-securities-step.component';
import { DoNotTradeStepComponent } from './components/do-not-trade-step/do-not-trade-step.component';
import { ProtectedCashStepComponent } from './components/protected-cash-step/protected-cash-step.component';
import { ModelAllocationStepComponent } from './components/model-allocation-step/model-allocation-step.component';
import { AttachFilesStepComponent } from './components/attach-files-step/attach-files-step.component';
import { ReviewSubmitStepComponent } from './components/review-submit-step/review-submit-step.component';
import { deriveClientName, ReallocationV2DataService, ReallocationV2LoadStage } from './services/reallocation-v2-data.service';
import {
  AccountRequestType,
  REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED,
  REALLOCATION_V2_REQUEST_NOTES_ENABLED,
  ReallocationV2Store,
  ReallocationV2StepId,
} from './state/reallocation-v2.store';
import { RequestTypeEnum } from '../../account-request-workflows/shared/enums/account-update.enum';

/** Per-request-type step-label overrides, applied over the exhaustive default map. */
type StepLabelOverrides = Partial<Record<AccountRequestType, Partial<Record<ReallocationV2StepId, string>>>>;

/**
 * Root of the Reallocation workflow (V2, ACMTECH-902).
 *
 * Self-contained standalone component: it owns its signal store + data service
 * and reuses only the shared confirmation modal / exit-copy for integration.
 * Rendered inline by the legacy "New Service Request" modal, so it shares that
 * dialog's `MatDialogRef` (used to size and close the whole modal).
 */
@Component({
  selector: 'app-reallocation-v2-request',
  standalone: true,
  imports: [
    CommonModule,
    StepIndicatorComponent,
    CalculationStripComponent,
    FundingStepComponent,
    SleeveAmountStepComponent,
    ReviewPositionsStepComponent,
    BuySecuritiesStepComponent,
    DoNotTradeStepComponent,
    ProtectedCashStepComponent,
    ModelAllocationStepComponent,
    AttachFilesStepComponent,
    ReviewSubmitStepComponent,
  ],
  providers: [ReallocationV2Store, ReallocationV2DataService],
  templateUrl: './reallocation-v2-request.component.html',
  animations: [fadeIn],
})
export class ReallocationV2RequestComponent implements OnInit, AfterViewInit {
  /** Account chosen in the picker. Accepted for integration; data currently
   *  comes from the static JSON seam regardless. */
  readonly account = input<AccountModel>();
  readonly workflow = input<AccountWorkflowNavModel>();
  readonly solicitation = input<SolicitationOptionModel>();
  /** Emitted to return to the request-type selection screen. */
  readonly canceled = output<void>();

  readonly store = inject(ReallocationV2Store);

  /**
   * Client name for the loading panel, from the `account` INPUT.
   *
   * The same derivation the header gets from `data().account`, so the line does not change
   * shape when the load completes and the real header replaces it.
   */
  readonly loadingClientName = computed(() => {
    const acct = this.account();
    return acct ? deriveClientName(acct) : '';
  });
  private readonly dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);

  /** Display name per step id; the indicator's labels are derived from the store's
   *  step list so the Attach Files flag can't leave them out of step with it. */
  private static readonly stepLabelsById: Record<ReallocationV2StepId, string> = {
    funding: 'Funding',
    sleeveAmounts: 'Allocate to Models',
    // 🔴 BACK TO WHAT IT WAS (AndyP, 2026-10-29). It was briefly 'Raise Cash', on the reasoning
    // that the step had become about the whole account -- the models it holds as well as the cash
    // and securities outside them -- and that raising money from either was one of the things you
    // could do there rather than the whole of it. Managed Models has since moved to Model
    // Allocations, so the step is positions and custodial cash again and the old name fits it.
    //
    // ⚠️ The Distribution override in `stepLabelOverrides` is unaffected and still wins
    // ('Raise Cash from Custom Holdings'): that request never had a Managed Models table, so its
    // step really is only about custom holdings.
    positions: 'Review/Sell Positions',
    buys: 'Buy Additional Securities',
    doNotTrade: 'Do Not Trade',
    protectedCash: 'Allocate Set-Aside Cash',
    // ACMTECH-1039: it is two sections now -- raising cash out of the models, then placing
    // what the account has across them -- so the label names the subject rather than one of
    // the two acts. 'Allocate to Models' survives as the second section's own heading, and as
    // `sleeveAmounts` above, which really is only that.
    allocation: 'Model Allocations',
    // the shared step carries files and/or notes, so its label follows the flags
    files:
      REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED && REALLOCATION_V2_REQUEST_NOTES_ENABLED
        ? 'Files & Notes'
        : REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED
          ? 'Documents'
          : 'Request Notes',
    review: 'Review & Submit',
  };

  /**
   * Steps whose HEADING above the body differs from the chip in the indicator, on purpose.
   *
   * 🔴 THE ONE SANCTIONED WAY FOR THOSE TWO TO DISAGREE. {@link labelFor} exists because they
   * once disagreed by accident -- `currentStepLabel` read `stepLabelsById` directly and skipped
   * the type overrides, so a distribution's indicator and heading named the same step two
   * different things (AndyP, 2026-09-25). Declaring the exception here keeps that fix intact: the
   * divergence is a single entry somebody chose, not two code paths that drifted.
   *
   * ⚠️ Applied over {@link stepLabelOverrides}, so a step may be renamed per type AND carry a
   * different heading. Nothing does both today.
   *
   * 'Documents' is the chip, which has to stay short enough not to wrap in the indicator;
   * 'Supporting Documents (Optional)' is the heading, where there is room to say what sort
   * (AndyP, 2026-10-01). This is every request type -- there is no per-type variation here.
   *
   * ⚠️ '(Optional)' LIVES HERE, NOT IN THE DESCRIPTION (AndyP, 2026-10-02). The step's own blurb
   * opened with "Optional:" and the two said it twice, a line apart. The heading is the better
   * home: it is read first, and an advisor scanning the stepper for what they still owe gets the
   * answer without reading the sentence underneath.
   *
   * 🟢 The chip stays 'Documents', deliberately -- the indicator has no room for the qualifier,
   * and a step that is optional is not a different step.
   */
  private static readonly stepHeadingOverrides: Partial<Record<ReallocationV2StepId, string>> = {
    files: 'Supporting Documents (Optional)',
  };

  /**
   * Step labels that read differently depending on which request is being filed.
   *
   * Applied over {@link stepLabelsById}, which stays the exhaustive default so a new step id
   * is still a compile error rather than a blank chip in the indicator. Only the steps whose
   * *meaning* changes with the type appear here — three do:
   *
   * - **funding** names the money's direction, which is the whole point of the step.
   * - **sleeveAmounts** is one component in two modes; "Allocate to" and "Raise Cash from"
   *   are opposite operations and the label is the only thing on screen that says which.
   * - **positions** is reused verbatim from the reallocation wizard, where unchecked rows move
   *   into Custom Holdings. In a distribution nothing moves — the checked rows are sold to
   *   raise cash — so "Review/Sell" would describe a step that no longer exists.
   */
  private static readonly stepLabelOverrides: StepLabelOverrides = {
    [AccountRequestType.Contribution]: {
      funding: 'Contribution Amount',
      sleeveAmounts: 'Allocate to Models',
    },
    [AccountRequestType.Distribution]: {
      // ⚠️ 'Amount', not 'Funding', and NOT 'Distribution Amount and Delivery' (AndyP,
      // 2026-09-30). Two changes on the same day, and the second undid a rule the first left
      // standing: the title used to name both halves of the step -- the amount on the left, the
      // delivery method, bank account and frequency on the right -- on the reasoning that naming
      // only the amount left the right column looking like something extra.
      //
      // That is now handled a level down. The step heads its own two columns ('Amount' and
      // 'Delivery'), so the right half is captioned where it lives and the title no longer has to
      // carry it. Paired with the contribution's 'Contribution Amount', which has no delivery
      // half at all, the two types now read as the same step rather than two different ones.
      funding: 'Distribution Amount',
      // ⚠️ 'Raise Cash from Models', not 'Distribute from Models' (AndyP, 2026-09-30). The money
      // taken from a sleeve here is not distributed BY this step — it is raised into the pool the
      // funding amount is paid out of, and an advisor reading "Distribute from Models" reasonably
      // expected these boxes to be the payout itself.
      sleeveAmounts: 'Raise Cash from Models',
      // ⚠️ 'Sell Positions' named the MECHANISM; this names the SOURCE, matching the step before
      // it ('Raise Cash from Models'). The two steps are the same question asked of two places,
      // and labelling one by where the money comes from and the other by what happens to the
      // securities made them read as unrelated (AndyP, 2026-09-22).
      positions: 'Raise Cash from Custom Holdings',
    },
  };

  /**
   * The load's three waves, in order, for the opening progress list.
   *
   * These are the real request boundaries rather than a timed animation, so the list
   * cannot show a stage complete while its calls are still out. Labels name what the
   * advisor is waiting for, not the endpoints.
   */
  readonly loadStages: readonly { id: ReallocationV2LoadStage; label: string }[] = [
    { id: 'account', label: 'Looking up account' },
    { id: 'positions', label: 'Loading positions' },
    { id: 'sleeves', label: 'Loading sleeves' },
  ];

  /**
   * How far the load has got, as a percentage, for the progress bar.
   *
   * Honest rather than animated: the stages are real request boundaries, so the
   * fraction is known. The in-flight stage counts as half, which keeps the bar moving
   * on each transition instead of sitting still through a whole wave.
   */
  loadPercent(): number {
    const order = this.loadStages.map((s) => s.id);
    const current = this.store.loadStage();
    if (current === 'done') return 100;
    const index = order.indexOf(current);
    if (index < 0) return 0;
    return ((index + 0.5) / order.length) * 100;
  }

  /** Whether a stage is finished, in flight, or not started. */
  loadStageState(id: ReallocationV2LoadStage): 'done' | 'active' | 'pending' {
    const order = this.loadStages.map((s) => s.id);
    const current = this.store.loadStage();
    // 'done' sits past the end of the list, so everything reads as finished.
    const currentIndex = current === 'done' ? order.length : order.indexOf(current);
    const index = order.indexOf(id);
    if (index < currentIndex) return 'done';
    return index === currentIndex ? 'active' : 'pending';
  }

  /**
   * A computed, not a field: the step list depends on the request type, so the labels
   * have to follow it rather than being fixed at construction.
   */
  readonly stepLabels = computed(
    () => this.store.steps().map((id) => this.labelFor(id)),
  );

  /**
   * One step's label, overrides applied.
   *
   * 🔴 SHARED WITH {@link currentStepLabel}, WHICH USED TO SKIP THE OVERRIDES. It read
   * `stepLabelsById` directly, so on a DISTRIBUTION the indicator said "Distribute from Models"
   * (the override now reads "Raise Cash from Models") while the heading a few pixels below it
   * said "Allocate to Models" -- the generic name of a step the overrides exist to rename
   * (AndyP, 2026-09-25).
   *
   * ⚠️ Two readers, one rule, so the indicator and the heading cannot disagree again. That was
   * the whole point of `stepLabelOverrides`, and half of it was being bypassed.
   */
  private labelFor(id: ReallocationV2StepId): string {
    const overrides = ReallocationV2RequestComponent.stepLabelOverrides[this.store.requestType()] ?? {};
    return overrides[id] ?? ReallocationV2RequestComponent.stepLabelsById[id];
  }

  /**
   * The current step's name, for the heading above the step body.
   *
   * ⚠️ ONE SOURCE. Four steps used to hardcode their own `<h3>` repeating, word for word, the
   * label the indicator was already showing highlighted a few pixels above -- and step 1 simply
   * had none, so the wizard was inconsistent as well as redundant. Worse, the duplication drifted:
   * renaming Allocate Protected Cash to Allocate Set-Aside Cash on 2026-09-15 meant editing the
   * step list AND that step's template, and forgetting either would have left two names for one
   * step on one screen.
   *
   * 🟢 The heading is kept rather than dropped because the calculation strip sits between the
   * indicator and the body. It is a full-width band of figures and it genuinely breaks the
   * connection, so the body needs re-anchoring after it -- which is the one thing the indicator
   * alone cannot do.
   *
   * ⚠️ The DESCRIPTIONS stay in their own step templates. Those are not redundant: they say what
   * to do, which a step name cannot. Only the title moved.
   *
   * 🟢 {@link stepHeadingOverrides} is read here and NOWHERE ELSE, which is what makes it a
   * heading rule rather than a second label map. Everything else still comes through
   * {@link labelFor}, so the indicator and the heading agree on every step but the declared one.
   */
  readonly currentStepLabel = computed(() => {
    const id = this.store.currentStepId();
    return ReallocationV2RequestComponent.stepHeadingOverrides[id] ?? this.labelFor(id);
  });

  /**
   * Which request the advisor came in to file, taken from the tile they clicked.
   *
   * `workflow` was already bound by the modal on every wizard branch and read by none of
   * them, so the Initial Account tile could be pointed at this component without adding
   * an input or touching the modal's bindings — and the same holds for every type added
   * since: the modal's four bindings have never changed.
   *
   * A lookup rather than a chain of ternaries. The tile is the ONLY thing that says which
   * request this is, and an unmapped tile has to fail visibly rather than fall through to
   * whichever branch happens to be last — see {@link requestTypeFromWorkflow}.
   */
  // Keyed by `number`, not `RequestTypeEnum`: `AccountWorkflowNavModel.order` is declared
  // `number`, so a Record keyed on the enum cannot be indexed with it without a cast — and a
  // cast here would be asserting the very thing the lookup exists to check.
  private static readonly requestTypeByNavOrder: Partial<Record<number, AccountRequestType>> = {
    [RequestTypeEnum.initialAccountSetup]: AccountRequestType.InitialAccount,
    [RequestTypeEnum.reallocationV2]: AccountRequestType.Reallocation,
    [RequestTypeEnum.contributionV2]: AccountRequestType.Contribution,
    [RequestTypeEnum.distributionV2]: AccountRequestType.Distribution,
  };

  /**
   * The request type this wizard was opened to file.
   *
   * Falls back to Reallocation, matching the store's own default: this wizard's tile is the
   * common entry point, and a caller that forgets the input gets the behaviour the wizard had
   * before the modes existed rather than a blank screen.
   */
  readonly requestType = computed(() => {
    const order = this.workflow()?.order;
    return (order != null
      ? ReallocationV2RequestComponent.requestTypeByNavOrder[order]
      : undefined) ?? AccountRequestType.Reallocation;
  });

  readonly isInitialAccount = computed(
    () => this.requestType() === AccountRequestType.InitialAccount,
  );

  /**
   * Header title, and the name used when confirming the submit.
   *
   * Close Account gets its own title rather than "Distribution": it is the one case where the
   * title is the clearest warning on screen that this request ends the account, and it is a
   * checkbox on step 1 that puts the wizard there — easy to have ticked and moved past.
   */
  readonly title = computed(() => {
    if (this.store.isCloseAccount()) return 'Close Account';
    switch (this.requestType()) {
      case AccountRequestType.InitialAccount:
        return 'Initial Account Setup';
      case AccountRequestType.Contribution:
        return 'Contribution';
      case AccountRequestType.Distribution:
        return 'Distribution';
      default:
        return 'Reallocation';
    }
  });

  ngOnInit(): void {
    // Set BEFORE initialize(): the step list is derived from it, so a later assignment
    // would render the indicator once with the wrong steps.
    this.store.requestType.set(this.requestType());
    // "Who initiated this request" is chosen on the picker page; carry it into
    // the submit payload like the other request workflows do.
    this.store.isSolicited.set(this.solicitation()?.isSolicited ?? false);
    // The account carries the advisor agent ids (real model lookup) and the
    // account identity shown in the header.
    this.store.initialize(this.account());
  }

  ngAfterViewInit(): void {
    // Sized to give the dense position/allocation tables room at the larger
    // text scale; capped to the viewport so it never overflows on smaller
    // displays. Height is FIXED (not content-driven) so the modal doesn't
    // resize/jump between steps — the header (with the step indicator) and
    // footer stay put while the step body scrolls internally.
    this.dialogRef.updateSize('min(132rem, 95vw)', 'min(84rem, 95vh)');
  }

  /**
   * Explain a step the SMA lock has closed.
   *
   * The indicator dims those steps but keeps them clickable, and reports the click here rather
   * than navigating -- a dimmed control that does nothing when clicked is the failure this whole
   * pattern exists to avoid.
   *
   * ⚠️ Same copy as the dimmed Add Model button, from {@link ReallocationV2Store.smaLockMessage},
   * because the advisor meets the same wall in two places and should read the same sentence. The
   * width matches the allocation step's SMA dialogs: `mat-dialog-container` has no width of its
   * own, so a paragraph this long would otherwise stretch the panel across the viewport.
   */
  onLockedStep(): void {
    this.dialog.open<ConfirmationModalComponent, ConfirmationModalData, boolean>(
      ConfirmationModalComponent,
      {
        width: '56rem',
        maxWidth: '90vw',
        data: {
          title: 'This account is a Separately Managed Account',
          message: this.store.smaLockMessage(),
          hideCancelButton: true,
          confirmationButtonText: 'OK',
        },
      },
    );
  }

  /** Close the modal outright, no exit-confirmation (used from the error state). */
  dismiss(): void {
    this.dialogRef.close(false);
  }

  /** Close (X): confirm unless already complete, then close the whole modal. */
  closeRequest(): void {
    if (this.store.isComplete()) {
      this.dialogRef.close(false);
      return;
    }
    this.dialog
      .open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      data: RequestExitConfirmationData,
    })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) this.dialogRef.close(false);
      });
  }
}
